/**
 * Toast Context
 * Global state management for toast notifications
 * Provides addToast and removeToast functions through React Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Toast, ToastType } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

interface ToastContextType {
  toasts: Toast[];
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// ============================================================================
// TOAST PROVIDER COMPONENT
// ============================================================================

interface ToastProviderProps {
  children: React.ReactNode;
}

/**
 * Toast Provider component
 * Wraps the application to provide global toast management
 * Typically placed at the root of the app
 *
 * @component
 * @example
 * // In your App.tsx or index.tsx
 * <ToastProvider>
 *   <YourAppComponent />
 * </ToastProvider>
 *
 * @param props - ToastProviderProps
 * @returns JSX.Element
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Debug logging on mount
  useEffect(() => {
    logger.debug('ToastProvider mounted');

    return () => {
      logger.debug('ToastProvider unmounted');
    };
  }, []);

  // Debug logging when toasts change
  useEffect(() => {
    if (toasts.length > 0) {
      logger.debug(`Toast count changed: ${toasts.length}`, {
        toasts: toasts.map((t) => ({
          id: t.id,
          type: t.type,
          message: t.message,
        })),
      });
    }
  }, [toasts]);

  /**
   * Add a new toast notification
   * @param type - Type of toast (success, error, warning, info)
   * @param message - Message to display
   * @param duration - Optional duration in milliseconds (default: 3000)
   */
  const addToast = useCallback(
    (type: ToastType, message: string, duration?: number) => {
      const id = `toast-${Date.now()}-${Math.random()}`;

      logger.debug(`Adding toast`, {
        id,
        type,
        message,
        duration: duration || 3000,
      });

      const newToast: Toast = {
        id,
        type,
        message,
        duration: duration || 3000,
      };

      setToasts((prev) => [...prev, newToast]);

      // TODO: Add toast queue limit (e.g., max 5 toasts visible)
      // TODO: Add toast grouping by type
      // TODO: Add persistent toasts that don't auto-dismiss

      return id;
    },
    []
  );

  /**
   * Remove a toast by ID
   * @param id - Toast ID to remove
   */
  const removeToast = useCallback((id: string) => {
    logger.debug(`Removing toast`, { id });

    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Clear all toasts
   */
  const clearAllToasts = useCallback(() => {
    logger.debug(`Clearing all toasts`);
    setToasts([]);
  }, []);

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

ToastProvider.displayName = 'ToastProvider';

// ============================================================================
// HOOK EXPORT
// ============================================================================

/**
 * Hook to use toast notifications
 * Must be used within a ToastProvider
 *
 * @returns ToastContextType with toast management functions
 * @throws Error if used outside of ToastProvider
 *
 * @example
 * const { addToast, removeToast, toasts } = useToast();
 * addToast('success', 'Item saved successfully!');
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);

  if (context === undefined) {
    logger.error(
      'useToast must be used within a ToastProvider'
    );
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};

/**
 * Shorthand hook for adding toasts
 * Useful when you only need to add notifications
 *
 * @returns Function to add toast with signature (type, message, duration?)
 *
 * @example
 * const addToast = useAddToast();
 * addToast('error', 'Something went wrong!');
 */
export const useAddToast = (): ((
  type: ToastType,
  message: string,
  duration?: number
) => void) => {
  const { addToast } = useToast();
  return addToast;
};

export default ToastContext;
